import {
    isNonEmptyArray,
    isNonEmptyObject,
    isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";
import {
    dateReadFormatter,
    formatDatesInObject,
    filterCertificationTasks,
    filterUserReviewTasks,
    notifyError,
    assembleFormSectionsAndElements,
    encodeSearchTerms,
} from "utils";
import {
    SELECT_ALL_KEY,
    FORM_OUTCOME_COMPLETE,
    IS_DEV,
	FM_PRODUCT_KEY,
} from "globals/constants";
import { api } from "services";
import store from "store/store";

export const filterAwardReviewTasks = (award, userTasks) => {
    if (isNonEmptyObject(award) && isNonEmptyArray(userTasks)) {
        if (award.awardId) {
            const userReviewTasks = filterUserReviewTasks(userTasks);

            return userReviewTasks.filter(
                ({ submissionId, submissionType }) =>
                    submissionType === "award" && submissionId === award.awardId
            );
        }
    }

    return [];
};

export const filterAwardCertificationTasks = (award, awardTasks) => {
    const hasAward = isNonEmptyObject(award);
    const hasTasks = isNonEmptyArray(awardTasks);
    const hasAwardId = isNonEmptyString(award.awardId);

    return hasAward && hasTasks && hasAwardId
        ? filterCertificationTasks(awardTasks)
        : [];
};

export const onAwardOutcomePressed = async (values, outcome, award) => {
    try {
        await api.tasks.saveTask(
            award?.formTaskId,
            { values, outcome: outcome || FORM_OUTCOME_COMPLETE },
            { numRetries: 0, notifyUponError: false }
        );

        window.dispatchEvent(
            new CustomEvent("form:outcomePressed", { detail: true })
        );
    } catch (error) {
        const message = IS_DEV
            ? error.message
            : store.getState().dictionary.proposals.routing.errors
                  .routeReviewFailed;

        window.dispatchEvent(
            new CustomEvent("form:outcomePressed", { detail: false })
        );
        notifyError(message, error);
    }
};

export const getTotalAwards = ({ searchTerms }) => {
    return findAwards({
        pageLimit: 1,
        pageOffset: 0,
        searchTerms,
    }).then(({ totalItems }) => {
        return totalItems;
    });
};

export const fetchAward = async (awardId) => {
    const award = await api.awards.get(awardId);

    const { formElements, formSections } = assembleFormSectionsAndElements(
        award.formElements
    );

    return { ...award, formElements, formSections };
};

export const findAwards = async ({
    pageLimit: incomingPageLimit,
    pageOffset,
    searchTerms,
    fieldKeys,
}) => {
    const pageLimit =
        incomingPageLimit === SELECT_ALL_KEY
            ? await getTotalAwards({ searchTerms })
            : incomingPageLimit;

    if (pageLimit === 0) {
        return Promise.resolve({ items: [], totalItems: 0 });
    }

    const data = await api.awards.find({
        page: parseInt(pageOffset),
        size: parseInt(pageLimit),
        search: encodeSearchTerms(searchTerms),
        fields: isNonEmptyArray(fieldKeys) ? fieldKeys.join(",") : "",
    });

    const totalItems = data?.totalElements;
    const awardFeedItems = data?.content;
    const hasAwardFeedItems = isNonEmptyArray(awardFeedItems);

    const mappedColumnValues = awardFeedItems.map((awardFeedItem) => {
        const hasColumnValues = isNonEmptyObject(awardFeedItem.columnValues);

        if (!hasColumnValues) {
            return {};
        }

        return {
            ...formatDatesInObject(
                awardFeedItem.columnValues,
                dateReadFormatter
            ),
            subRows: isNonEmptyObject(awardFeedItem.modifications)
                ? awardFeedItem.modifications.map((subFeed) => {
                      const subValueMap = {
                          ...formatDatesInObject(
                              subFeed.columnValues,
                              dateReadFormatter
                          ),
                          adminTeamMemberData: subFeed.adminTeamMembers,
                          modificationStatus: subFeed.modificationStatus,
                      };

                      return subValueMap;
                  })
                : [],
            adminTeamMemberData: awardFeedItem.adminTeamMembers,
        };
    });

    const items = hasAwardFeedItems ? mappedColumnValues : [];

    return { items, totalItems };
};

export const getCaseInstanceId = (items) => {
    const item = items.reduce((prev, current) =>
        prev.modificationNumber < current.modificationNumber ? prev : current
    );

    return item.caseInstanceId;
};

export const getFundManagerInstanceURL = async (tenantId) => {
    const response = await api.tenant.getTenantProducts(tenantId, 100);
	const fundManagerProduct = response._embedded.tenant_products.find(
        (product) => product.name === FM_PRODUCT_KEY && product.active
    );
    
    if (!fundManagerProduct){
        return null;
    }
    
    try {
        const url = new URL(fundManagerProduct.instanceURL);
        return url.origin;
    } catch (error) {
        console.error("Cannot parse Fund Manager URL");
        return null;
    }
};
